import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { hasAnyPermission, hasModuleAccess, type AuthUser } from '../../lib/auth/permissions';
import LoadingSpinner from '../common/LoadingSpinner';

export interface PermissionRouteProps {
  children?: React.ReactNode;
  permission?: string;
  permissions?: string[];
  module?: string;
  getAuthToken: () => string | null;
  fetchUser: () => Promise<AuthUser>;
  loginPath?: string;
  noAccessPath?: string;
  fallback?: React.ReactNode;
}

const EMPTY_PERMISSIONS: string[] = [];

const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  permission,
  permissions = EMPTY_PERMISSIONS,
  module,
  getAuthToken,
  fetchUser,
  loginPath,
  noAccessPath = '/panel/no-access',
  fallback,
}) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (allowed === true) return;

    const resolvedLogin = loginPath ?? '/login';

    const token = getAuthToken();
    if (!token) {
      window.location.replace(resolvedLogin.startsWith('/') ? resolvedLogin : `/${resolvedLogin}`);
      return;
    }

    fetchUser()
      .then((loadedUser) => {
        const requiredPermissions = permission ? [permission, ...permissions] : permissions;
        let isAllowed = true;

        if (module && !hasModuleAccess(loadedUser, module)) {
          isAllowed = false;
        }

        if (requiredPermissions.length > 0 && !hasAnyPermission(loadedUser, requiredPermissions)) {
          isAllowed = false;
        }

        if (!isAllowed) {
          window.location.replace(noAccessPath);
          return;
        }

        setAllowed(true);
      })
      .catch(() => {
        window.location.replace(resolvedLogin.startsWith('/') ? resolvedLogin : `/${resolvedLogin}`);
      })
      .finally(() => setLoading(false));
  }, [allowed, permission, permissions, module, getAuthToken, fetchUser, loginPath, noAccessPath]);

  if (loading || allowed !== true) {
    return (
      fallback ?? (
        <LoadingSpinner variant="fullscreen" message="در حال بررسی دسترسی..." />
      )
    );
  }

  return children ? <>{children}</> : <Outlet />;
};

export default PermissionRoute;
